import type {ChangeSpec} from '@codemirror/state'
import type {Command, EditorView} from '@codemirror/view'

import {MarkdownLine} from '../../../language-tools/markdown/markdown-line'
import {getNodesOverlappingRange} from '../../../language-tools/syntax-tree/node-range-utils'
import {isPositionedNode, type PositionedNode} from '../../../language-tools/syntax-tree/positioned-node'
import {getMarkdownTree} from '../../extensions/markdown-parser'
import {createText} from '../../extensions/single-line-editor'
import {getDelimiters, getExistingFormatting} from '../inline-formatting/utils'

const CODE_BLOCK_PREFIX = '```\n'
const CODE_BLOCK_SUFFIX = '\n```'

export const formatCodeBlock: Command = (view: EditorView) => {
  if (!view.hasFocus) view.focus()

  const tree = getMarkdownTree(view.state)
  const source = view.state.doc.toString()
  const selection = view.state.selection.main
  const {from, to} = selection

  // If the selection is inside a single code block, remove the code block formatting
  const codeNode = getExistingFormatting(view, 'code')
  if (codeNode) {
    // For code blocks, we need to remove the delimiters and the newlines around them (after opening fence and before closing fence)
    if (!isPositionedNode(codeNode)) return false
    if (codeNode.type !== 'code') return false

    const delimiters = getDelimiters(codeNode).filter(isPositionedNode) as PositionedNode[]
    if (delimiters.length === 0) return false

    const changes: ChangeSpec[] = delimiters.map((delimiter, i) => {
      const isFirstDelimiter = i === 0
      const isLastDelimiter = i === delimiters.length - 1
      // Remove the opening fence and the newline after it, and the closing fence and the newline before it
      return {
        from: isLastDelimiter ? delimiter.position.start.offset - 1 : delimiter.position.start.offset,
        to: isFirstDelimiter ? delimiter.position.end.offset + 1 : delimiter.position.end.offset,
      }
    })

    view.dispatch({
      changes,
      userEvent: 'input',
      scrollIntoView: true,
    })
    return true
  }

  // Otherwise, wrap the selection in a new code block
  const changes: ChangeSpec[] = []

  // Collect all existing code blocks that overlap with the selection
  const codeBlockNodes = [...getNodesOverlappingRange(tree, from, to)].filter(node => node.type === 'code')

  // Remove delimiters from existing code blocks so we can wrap everything in one code block
  const codeBlockDelimiterPositions: ChangeSpec[] = codeBlockNodes.flatMap(codeBlockNode => {
    const delimiters = getDelimiters(codeBlockNode).filter(isPositionedNode) as PositionedNode[]
    return delimiters.map((delimiter, i) => {
      const isFirstDelimiter = i === 0
      const isLastDelimiter = i === delimiters.length - 1
      // "code" nodes don't include the suffix line ending delimiter, so we handle that here
      // Also, the opening fence is followed by a newline that's not a delimiter, so we include it
      return {
        from: isLastDelimiter ? delimiter.position.start.offset - 1 : delimiter.position.start.offset,
        to: isFirstDelimiter ? delimiter.position.end.offset + 1 : delimiter.position.end.offset,
      }
    })
  })

  const startOfFirstCodeBlock = (codeBlockDelimiterPositions.at(0) as {from: number; to: number} | undefined)?.from
  const endOfLastCodeBlock = (codeBlockDelimiterPositions.at(-1) as {from: number; to: number} | undefined)?.to
  changes.push(...codeBlockDelimiterPositions)

  // Determine the range to wrap in code block delimiters
  const lines = MarkdownLine.linesAtRange(tree, source, from, to)
  const firstLineStart = lines[0]?.isOffsetAfterPrefixes(from) ? lines[0]?.contentStart : lines[0]?.start
  const lastLineEnd = lines.at(-1)?.end

  // Expand range to include any existing code blocks
  const start =
    startOfFirstCodeBlock !== undefined && firstLineStart !== undefined
      ? Math.min(firstLineStart, startOfFirstCodeBlock)
      : firstLineStart
  const end =
    endOfLastCodeBlock !== undefined && lastLineEnd !== undefined
      ? Math.max(lastLineEnd, endOfLastCodeBlock)
      : lastLineEnd

  if (start === undefined || end === undefined) return false

  changes.push({from: start, insert: createText(CODE_BLOCK_PREFIX)}, {from: end, insert: createText(CODE_BLOCK_SUFFIX)})
  const isBlankLine = selection.empty && MarkdownLine.atOffset(tree, source, selection.from)?.isBlank
  view.dispatch({
    changes,
    // Handle caret placement for blank line
    selection: isBlankLine ? {anchor: start + CODE_BLOCK_PREFIX.length} : undefined,
    userEvent: 'input',
    scrollIntoView: true,
  })

  return true
}
